import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import * as XLSX from 'xlsx';
import { Upload, Download, AlertCircle, CheckCircle2, Loader2 } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { createSlugFromHebrew } from '@/lib/slugUtils';

interface ProductBulkImportProps {
  isOpen: boolean;
  onClose: () => void;
  onImportComplete: () => void;
}

interface ImportProduct {
  name: string;
  description: string;
  sale_type: 'immediate' | 'liquidation';
  group_buy_enabled: boolean;
  liquidation_sale_enabled: boolean;
  category_name: string;
  subcategory_name: string;
  price_per_unit: number;
  cost_price?: number;
  original_price?: number;
  measurement_unit: string;
  package_coverage_per_unit?: number;
  total_inventory: number;
  min_quantity: number;
  max_quantity?: number;
  step_quantity: number;
  city: string;
  supplier_name: string;
  supplier_sku?: string;
  sku?: string;
  material?: string;
  finish?: string;
  color_family?: string;
  dimension_width?: number;
  dimension_width_unit?: string;
  dimension_height?: number;
  dimension_height_unit?: string;
  dimension_depth?: number;
  dimension_depth_unit?: string;
  country_of_origin?: string;
  notes?: string;
  grade?: 'A' | 'B' | 'C' | 'D';
}

const ProductBulkImport = ({ isOpen, onClose, onImportComplete }: ProductBulkImportProps) => {
  const [file, setFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState<{ success: number; failed: number; errors: string[] } | null>(null);

  const downloadTemplate = () => {
    const template = [
      {
        'שם_מוצר': 'דוגמה - פרקט אלון רגיל',
        'תיאור': 'פרקט אלון איכותי למכירה רגילה',
        'סוג_מכירה': 'רגיל',
        'קטגוריה': 'פרקטים',
        'תת_קטגוריה': 'פרקט עץ',
        'מחיר_ליחידה_או_לחבילה': 150.00,
        'מחיר_עלות': 100.00,
        'מחיר_מקורי': 200.00,
        'יחידת_מידה': 'מ"ר',
        'מ"ר_בחבילה': 2.5,
        'כמות_במלאי': 500,
        'כמות_מינימום': 5,
        'כמות_מקסימום': 100,
        'כמות_קפיצה': 5,
        'עיר': 'תל אביב',
        'שם_ספק': 'ספק דוגמה בע״מ',
        'מקט_ספק': 'SUP-1001',
        'מקט_פנימי': 'INT-1001',
        'חומר': 'עץ אלון',
        'גימור': 'מט',
        'משפחת_צבע': 'חום',
        'רוחב': 18,
        'יחידת_רוחב': 'cm',
        'אורך': 120,
        'יחידת_אורך': 'cm',
        'עובי': 10,
        'יחידת_עובי': 'mm',
        'ארץ_ייצור': 'גרמניה',
        'הערות': 'מוצר במלאי שוטף',
        'דרגה': 'A'
      },
      {
        'שם_מוצר': 'דוגמה - גרניט פורצלן (חיסול)',
        'תיאור': 'אריחי גרניט פורצלן במכירת חיסול',
        'סוג_מכירה': 'מכירת חיסול',
        'קטגוריה': 'ריצוף חוץ',
        'תת_קטגוריה': 'גרניט פורצלן',
        'מחיר_ליחידה_או_לחבילה': 80.00,
        'מחיר_עלות': 60.00,
        'מחיר_מקורי': 120.00,
        'יחידת_מידה': 'מ"ר',
        'מ"ר_בחבילה': 1.44,
        'כמות_במלאי': 50,
        'כמות_מינימום': 20,
        'כמות_מקסימום': 50,
        'כמות_קפיצה': 1,
        'עיר': 'חיפה',
        'שם_ספק': 'ספק קרמיקה',
        'מקט_ספק': 'CER-999',
        'מקט_פנימי': 'LIQ-999',
        'חומר': 'גרניט פורצלן',
        'גימור': 'מניעת החלקה',
        'משפחת_צבע': 'אפור',
        'רוחב': 60,
        'יחידת_רוחב': 'cm',
        'אורך': 60,
        'יחידת_אורך': 'cm',
        'עובי': 9,
        'יחידת_עובי': 'mm',
        'ארץ_ייצור': 'ספרד',
        'הערות': 'כל המלאי חייב להימכר',
        'דרגה': 'B'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(template);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Products');
    XLSX.writeFile(wb, 'product_import_template.xlsx');

    toast.success('תבנית הורדה', {
      description: 'קובץ התבנית התחיל להוריד'
    });
  };

  const mapSaleType = (hebrewOrEnglish: string): 'immediate' | 'liquidation' => {
    if (!hebrewOrEnglish) return 'immediate';

    const normalized = hebrewOrEnglish.trim().toLowerCase();

    // Direct mappings
    if (['רגיל', 'מיידי', 'immediate', 'regular'].includes(normalized)) return 'immediate';
    if (['מכירת חיסול', 'מכירת_חיסול', 'חיסול', 'liquidation', 'clearance'].includes(normalized)) return 'liquidation';

    // Fuzzy matching
    if (normalized.includes('חיסול') || normalized.includes('liquidation')) return 'liquidation';

    return 'immediate';
  };

  const isGroupBuy = (hebrewOrEnglish: string): boolean => {
    if (!hebrewOrEnglish) return false;

    const normalized = hebrewOrEnglish.trim().toLowerCase();

    return normalized === 'קבוצת רכישה' ||
      normalized === 'קבוצת_רכישה' ||
      normalized === 'קב רכישה' ||
      normalized === 'group_buy' ||
      normalized === 'קבוצת-רכישה' ||
      normalized.includes('קבוצת רכישה');
  };

  const mapMeasurementUnit = (hebrewOrEnglish: string): string => {
    if (!hebrewOrEnglish) return 'unit';

    const normalized = hebrewOrEnglish.trim().toLowerCase();

    const mapping: Record<string, string> = {
      // Hebrew mappings
      'יחידה': 'unit',
      'יח': 'unit',
      'יח\'': 'unit',
      'מטר': 'meter',
      'מ': 'meter',
      'מטר רבוע': 'square_meter',
      'מ"ר': 'square_meter',
      'מר': 'square_meter',
      'חבילות (מ"ר)': 'package_by_square_meter',
      'חבילות מ"ר': 'package_by_square_meter',
      'חבילות': 'package_by_square_meter',
      'ק"ג': 'kilogram',
      'קג': 'kilogram',
      'קילוגרם': 'kilogram',
      'ליטר': 'liter',
      'ל': 'liter',
      'מטר מעוקב': 'cubic_meter',
      'מ"ק': 'cubic_meter',
      'משטח': 'surface',

      // English mappings (lowercase)
      'unit': 'unit',
      'meter': 'meter',
      'square_meter': 'square_meter',
      'kilogram': 'kilogram',
      'liter': 'liter',
      'cubic_meter': 'cubic_meter',
      'surface': 'surface',
      'package_by_square_meter': 'package_by_square_meter',
    };

    return mapping[normalized] || 'unit'; // Default to 'unit' if not found
  };

  // Robust key finder helper
  const findValueByKey = (row: any, keys: string[]) => {
    const rowKeys = Object.keys(row);
    // 1. Exact match
    for (const key of keys) {
      if (row[key] !== undefined) return row[key];
    }
    // 2. Case-insensitive and trimmed match
    for (const key of keys) {
      const normalizedKey = key.toLowerCase().trim();
      const foundKey = rowKeys.find(rk => rk.toLowerCase().trim() === normalizedKey);
      if (foundKey && row[foundKey] !== undefined) return row[foundKey];
    }
    return undefined;
  };

  const mapExcelToProduct = (row: any): ImportProduct => {
    console.log('Row Keys:', Object.keys(row));
    console.log('Processing raw row:', row);

    const getString = (keys: string[]) => {
      const val = findValueByKey(row, keys);
      return val ? String(val).trim() : '';
    };

    const getNumber = (keys: string[]) => {
      const val = findValueByKey(row, keys);
      if (typeof val === 'number') return val;
      if (typeof val === 'string') {
        const parsed = parseFloat(val);
        return isNaN(parsed) ? undefined : parsed;
      }
      return undefined;
    };

    // Define keys
    const nameKeys = ['שם_מוצר', 'שם מוצר', 'Product Name', 'name'];
    const descKeys = ['תיאור', 'description', 'Description'];
    const saleTypeKeys = ['סוג_מכירה', 'סוג מכירה', 'מכירה', 'Sale Type', 'sale_type'];
    const categoryKeys = ['קטגוריה', 'Category', 'category'];
    const subcategoryKeys = ['תת_קטגוריה', 'תת קטגוריה', 'Subcategory', 'subcategory'];
    const priceKeys = ['מחיר_ליחידה_או_לחבילה', 'מחיר_ליחידה', 'מחיר ליחידה', 'Price', 'price'];
    const costPriceKeys = ['מחיר_עלות', 'מחיר עלות', 'Cost Price', 'cost_price'];
    const originalPriceKeys = ['מחיר_מקורי', 'מחיר מקורי', 'Original Price', 'original_price'];
    const unitKeys = ['יחידת_מידה', 'יחידת מידה', 'Unit', 'measurement_unit'];
    const coverageKeys = ['מטר_רבוע_בחבילה', 'מ"ר בחבילה', 'מר בחבילה', 'מ"ר_בחבילה', 'גודל חבילה', 'sqm_per_package', 'package_coverage'];
    const inventoryKeys = ['כמות_במלאי', 'כמות במלאי', 'Inventory', 'stock'];
    const minKeys = ['כמות_מינימום', 'כמות מינימום', 'Min Quantity'];
    const maxKeys = ['כמות_מקסימום', 'כמות מקסימום', 'Max Quantity'];
    const stepKeys = ['כמות_קפיצה', 'כמות קפיצה', 'Step Quantity'];
    const cityKeys = ['עיר', 'City'];
    const supplierKeys = ['שם_ספק', 'שם ספק', 'Supplier'];
    const supplierSkuKeys = ['מקט_ספק', 'מק"ט ספק', 'Supplier SKU'];
    const skuKeys = ['מקט_פנימי', 'מק"ט פנימי', 'SKU', 'Internal SKU'];
    const materialKeys = ['חומר', 'Material'];
    const finishKeys = ['גימור', 'Finish'];
    const colorKeys = ['משפחת_צבע', 'משפחת צבע', 'Color'];
    const widthKeys = ['רוחב', 'Width'];
    const widthUnitKeys = ['יחידת_רוחב', 'יחידת רוחב', 'Width Unit'];
    const heightKeys = ['אורך', 'Length', 'Height'];
    const heightUnitKeys = ['יחידת_אורך', 'יחידת אורך', 'Length Unit'];
    const depthKeys = ['עובי', 'Thickness', 'Depth'];
    const depthUnitKeys = ['יחידת_עובי', 'יחידת עובי', 'Thickness Unit'];
    const countryKeys = ['ארץ_ייצור', 'ארץ ייצור', 'Country'];
    const notesKeys = ['הערות', 'Notes'];
    const gradeKeys = ['דרגה', 'דירוג מוצר', 'Grade'];

    const saleTypeValue = getString(saleTypeKeys);
    console.log('Detected sale type value:', saleTypeValue);
    if (saleTypeValue) {
      console.log('Sale type character codes:', saleTypeValue.split('').map((c: string) => c.charCodeAt(0)));
    }

    const name = getString(nameKeys);
    const category_name = getString(categoryKeys);
    const subcategory_name = getString(subcategoryKeys);

    const mappedSaleType = mapSaleType(saleTypeValue);

    return {
      name,
      description: getString(descKeys),
      sale_type: mappedSaleType,
      group_buy_enabled: mappedSaleType === 'immediate' ? isGroupBuy(saleTypeValue) : false,
      liquidation_sale_enabled: mappedSaleType === 'liquidation',
      category_name,
      subcategory_name,
      price_per_unit: getNumber(priceKeys) || 0,
      cost_price: getNumber(costPriceKeys),
      original_price: getNumber(originalPriceKeys),
      measurement_unit: mapMeasurementUnit(getString(unitKeys)),
      package_coverage_per_unit: getNumber(coverageKeys),
      total_inventory: Math.floor(getNumber(inventoryKeys) || 0),
      min_quantity: Math.floor(getNumber(minKeys) || 1),
      max_quantity: getNumber(maxKeys) ? Math.floor(getNumber(maxKeys)!) : undefined,
      step_quantity: Math.floor(getNumber(stepKeys) || 1),
      city: getString(cityKeys) || 'לא צוין',
      supplier_name: getString(supplierKeys),
      supplier_sku: getString(supplierSkuKeys),
      sku: getString(skuKeys),
      material: getString(materialKeys),
      finish: getString(finishKeys),
      color_family: getString(colorKeys),
      dimension_width: getNumber(widthKeys),
      dimension_width_unit: getString(widthUnitKeys) || 'cm',
      dimension_height: getNumber(heightKeys),
      dimension_height_unit: getString(heightUnitKeys) || 'cm',
      dimension_depth: getNumber(depthKeys),
      dimension_depth_unit: getString(depthUnitKeys) || 'mm',
      country_of_origin: getString(countryKeys),
      notes: getString(notesKeys),
      grade: (getString(gradeKeys) as 'A' | 'B' | 'C' | 'D') || 'B',
    };
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setFile(e.target.files[0]);
      setResults(null);
    }
  };

  const handleImport = async () => {
    if (!file) {
      toast.error('שגיאה', { description: 'אנא בחר קובץ להעלאה' });
      return;
    }

    setImporting(true);
    const errors: string[] = [];
    let successCount = 0;
    let failedCount = 0;

    try {
      const data = await file.arrayBuffer();
      const workbook = XLSX.read(data);
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      const jsonData = XLSX.utils.sheet_to_json(worksheet);

      // Fetch suppliers
      const { data: suppliersData } = await supabase
        .from('suppliers')
        .select('id, name');

      const suppliersList = suppliersData || [];

      console.log('Parsed Excel data:', jsonData);

      for (const [index, row] of jsonData.entries()) {
        try {
          const product = mapExcelToProduct(row);

          // Validate required fields
          if (!product.name || !product.name.trim()) {
            errors.push(`שורה ${index + 2}: שדה "שם_מוצר" חובה`);
            failedCount++;
            continue;
          }

          // Find or create category
          const { data: categories } = await supabase
            .from('categories')
            .select('id')
            .eq('name', product.category_name)
            .limit(1);

          if (!categories || categories.length === 0) {
            errors.push(`שורה ${index + 2}: קטגוריה "${product.category_name}" לא קיימת`);
            failedCount++;
            continue;
          }

          const category_id = categories[0].id;

          // Find or create subcategory
          const { data: subcategories } = await supabase
            .from('subcategories')
            .select('id')
            .eq('name', product.subcategory_name)
            .eq('category_id', category_id)
            .limit(1);

          if (!subcategories || subcategories.length === 0) {
            errors.push(`שורה ${index + 2}: תת-קטגוריה "${product.subcategory_name}" לא קיימת בקטגוריה "${product.category_name}"`);
            failedCount++;
            continue;
          }

          const subcategory_id = subcategories[0].id;

          // Create slug
          const slug = createSlugFromHebrew(product.name);

          // Find supplier ID
          let supplier_id = null;
          if (product.supplier_name) {
            const normalizedInput = product.supplier_name.trim().toLowerCase();
            const supplier = suppliersList.find(s =>
              s.name.trim().toLowerCase() === normalizedInput ||
              s.name.trim().toLowerCase().includes(normalizedInput) ||
              normalizedInput.includes(s.name.trim().toLowerCase())
            );
            if (supplier) {
              supplier_id = supplier.id;
            }
          }

          // Insert product
          const { error: insertError } = await supabase
            .from('products')
            .insert({
              name: product.name,
              description: product.description,
              sale_type: product.sale_type,
              group_buy_enabled: product.group_buy_enabled,
              category_id,
              subcategory_id,
              price_per_unit: product.price_per_unit,
              cost_price: product.cost_price,
              original_price: product.original_price,
              measurement_unit: product.measurement_unit,
              package_coverage_per_unit: product.package_coverage_per_unit,
              total_inventory: product.total_inventory,
              min_quantity: product.min_quantity,
              max_quantity: product.max_quantity,
              step_quantity: product.step_quantity,
              city: product.city,
              supplier_id: supplier_id,
              supplier_name: product.supplier_name,
              supplier_sku: product.supplier_sku,
              sku: product.sku,
              material: product.material,
              finish: product.finish,
              color_family: product.color_family,
              dimension_width: product.dimension_width,
              dimension_width_unit: product.dimension_width_unit,
              dimension_height: product.dimension_height,
              dimension_height_unit: product.dimension_height_unit,
              dimension_depth: product.dimension_depth,
              dimension_depth_unit: product.dimension_depth_unit,
              country_of_origin: product.country_of_origin,
              notes: product.notes,
              grade: product.grade,
              slug,
              is_active: true,
              liquidation_sale_enabled: product.liquidation_sale_enabled,
              images: [],
            });

          if (insertError) {
            console.error('Insert error:', insertError);
            errors.push(`שורה ${index + 2} (${product.name}): ${insertError.message}`);
            failedCount++;
          } else {
            successCount++;
          }
        } catch (error: any) {
          console.error('Row processing error:', error);
          errors.push(`שורה ${index + 2}: ${error.message}`);
          failedCount++;
        }
      }

      setResults({ success: successCount, failed: failedCount, errors });

      if (successCount > 0) {
        toast.success('ייבוא הושלם', {
          description: `${successCount} מוצרים יובאו בהצלחה${failedCount > 0 ? `, ${failedCount} נכשלו` : ''}`
        });
        onImportComplete();
      } else {
        toast.error('הייבוא נכשל', {
          description: 'לא הצלחנו לייבא אף מוצר. בדוק את השגיאות למטה.'
        });
      }
    } catch (error: any) {
      console.error('Import error:', error);
      toast.error('שגיאה בייבוא', {
        description: error.message || 'אירעה שגיאה בעת ניסיון לייבא את הקובץ'
      });
    } finally {
      setImporting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>ייבוא מוצרים ממסמך Excel</DialogTitle>
          <DialogDescription>
            העלה קובץ Excel עם פרטי המוצרים לייבוא המוני למערכת
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          <Alert>
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              לפני הייבוא, וודא שהקטגוריות ותתי-הקטגוריות כבר קיימות במערכת
            </AlertDescription>
          </Alert>

          <div className="flex gap-2">
            <Button onClick={downloadTemplate} variant="outline" className="flex-1">
              <Download className="mr-2 h-4 w-4" />
              הורד תבנית Excel לדוגמה
            </Button>
          </div>

          <div>
            <Label htmlFor="file">בחר קובץ Excel</Label>
            <Input
              id="file"
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              disabled={importing}
            />
            {file && (
              <p className="text-sm text-muted-foreground mt-2">
                קובץ נבחר: {file.name}
              </p>
            )}
          </div>

          {results && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-green-50 dark:bg-green-950/20 rounded-lg border border-green-200">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="h-5 w-5 text-green-600" />
                    <p className="font-medium">הצליחו: {results.success}</p>
                  </div>
                </div>
                <div className="p-4 bg-red-50 dark:bg-red-950/20 rounded-lg border border-red-200">
                  <div className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                    <p className="font-medium">נכשלו: {results.failed}</p>
                  </div>
                </div>
              </div>

              {results.errors.length > 0 && (
                <div className="space-y-2">
                  <h4 className="font-medium text-destructive">שגיאות:</h4>
                  <div className="max-h-60 overflow-y-auto space-y-1 p-3 bg-destructive/5 rounded-lg border border-destructive/20">
                    {results.errors.map((error, idx) => (
                      <p key={idx} className="text-sm text-destructive">• {error}</p>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={onClose} disabled={importing}>
              ביטול
            </Button>
            <Button onClick={handleImport} disabled={!file || importing}>
              {importing ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  מייבא...
                </>
              ) : (
                <>
                  <Upload className="mr-2 h-4 w-4" />
                  ייבא מוצרים
                </>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ProductBulkImport;
